/**
 * **How much of each cluster's week the open roles have already booked** — the first thing on the
 * Cluster View since Oct 19 2026 (Theresa).
 *
 * It is the prioritisation model, by cluster: every open role in Airtable, priced at the hours its
 * approved priority rating buys, against what that cluster's recruiters can actually commit. The
 * same content the Company View carries as its collapsed §03, open and first here — because this
 * is the question the leads arrive with, and it was a tab away.
 *
 * ⚠️ THE DISAGREEMENT WITH THE INDEX BELOW IS THE POINT, not a caveat (Theresa, Oct 19 2026:
 * *"I want the sections to disagree to show if we are acting against the priority ratings"*). The
 * hours here are what the ratings BOUGHT; the index below is built on what the team is actually
 * doing. When they part company the week has gone somewhere the ratings did not ask for — and
 * `PriorityAlignment` turns that into a figure rather than leaving it to be noticed.
 *
 * ⚠️ IT READS THE SHARED PRIORITISATION STATE, not the check-in sheet the rest of this tab reads.
 * So the hours here follow Airtable and the roster saved on Planning, while the role load and
 * interview figures below follow the TA Cockpit sheet and the warehouse. Two sources on one tab,
 * which the footer states — the same rule the Company View's own distribution section follows.
 */
import type { CheckInData } from '../../../types/taCheckin';
import { ClusterLoad } from '../rolePrioritisation/ClusterLoad';
import { useRolePrioritisationState } from '../rolePrioritisation/RolePrioritisationProvider';
import { PriorityAlignment } from './PriorityAlignment';

export function ClusterBookedLoad({ data }: { data?: CheckInData | null }) {
  const { state, clusterStatsList, model, cap, unratedByCluster } = useRolePrioritisationState();

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 className="text-base font-semibold text-gray-900">How booked each cluster is</h2>
        <span className="text-xs text-gray-500">
          Bars share one scale · blue marker = that cluster&apos;s committed capacity
        </span>
      </div>

      <ClusterLoad
        stats={clusterStatsList}
        state={state}
        model={model}
        cap={cap}
        unratedByCluster={unratedByCluster}
      />

      {/*
        ⚠️ The alignment block reads the CHECK-IN sheet for the interview counts, so it is handed
        the payload rather than reading the provider like everything above it. Absent (a sheet
        outage) it renders a line saying so, and the booked load above is untouched — two sources,
        one tab, each degrading on its own.
      */}
      <PriorityAlignment data={data} stats={clusterStatsList} model={model} />

      <p className="mt-2 text-xs leading-relaxed text-gray-500">
        Every open role in the hiring database, priced at the weekly hours its approved priority
        rating buys, against what each cluster&apos;s recruiters can commit. These are{' '}
        <b>absolute hours</b> — whether the work fits at all. The cluster index below is a{' '}
        <b>relative</b> score across the three clusters, so it always has a worst one: it says where
        to help first, not whether the workload is acceptable.
      </p>
    </section>
  );
}
